/**
 * EmailTinyMCE — TinyMCE lifecycle helpers for the email compose / reply forms.
 *
 * Replaces the inline TinyMCE init/remove logic in:
 *   - email_reply_form_controller.js
 *   - email_compose_controller.js (not used there — compose uses contenteditable)
 *
 * Key robustness behaviours:
 *   - double-rAF to ensure the DOM is ready before init
 *   - stale editor detection: removes a phantom instance whose DOM element
 *     is no longer in the document (happens across Turbo Frame navigations)
 *   - auto-save on change/input/keyup via editor.save()
 */

const CSRF_TOKEN = () =>
  document.querySelector('meta[name="csrf-token"]')?.content ?? ''

/**
 * Initialize a TinyMCE instance for a given textarea element.
 *
 * @param {HTMLTextAreaElement} editorEl  — the <textarea> to enhance
 * @param {object}             options    — { onImageUploadError }
 * @returns {Promise<void>}
 */
export function initTinyMCE(editorElOrSelector, { onImageUploadError, height, placeholder, initialContent, onInput, imagesUploadUrl, onSetup } = {}) {
  const editorEl = typeof editorElOrSelector === 'string'
    ? document.querySelector(editorElOrSelector)
    : editorElOrSelector
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(async () => {
      if (!editorEl || !window.tinymce) return resolve()

      const editorId = editorEl.id
      const existing = tinymce.get(editorId)

      // Remove stale instance whose DOM element is gone (Turbo navigation artifact)
      if (existing && !document.body.contains(existing.getElement())) {
        tinymce.remove(`#${editorId}`)
      }

      if (tinymce.get(editorId)) {
        // Already live — just focus it
        tinymce.get(editorId).focus()
        return resolve()
      }

      // Pre-fill textarea so TinyMCE picks up the content on init
      if (initialContent && editorEl.value === '') {
        editorEl.value = initialContent
      }

      const isMobile = window.innerWidth < 768

      const editorHeight = height || (isMobile ? 150 : 200)

      const uploadUrl = imagesUploadUrl || '/emails/image_upload'

      await tinymce.init({
        selector: `#${editorEl.id}`,
        license_key: 'gpl',
        promotion: false,
        branding: false,
        statusbar: false,
        menubar: false,
        language: 'hu_HU',
        toolbar: 'bold italic underline | forecolor | bullist numlist | link | image',
        toolbar_mode: isMobile ? 'sliding' : 'wrap',
        plugins: ['link', 'lists', 'image'],
        height: editorHeight,
        paste_data_images: true,
        automatic_uploads: true,
        images_upload_url: uploadUrl,
        images_upload_credentials: true,
        relative_urls: false,
        remove_script_host: false,
        convert_urls: true,
        images_upload_handler: async (blobInfo) => {
          const formData = new FormData()
          formData.append('file', blobInfo.blob(), blobInfo.filename())
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'X-CSRF-Token': CSRF_TOKEN() },
            body: formData,
          })
          if (!response.ok) {
            throw new Error(onImageUploadError?.() ?? 'Image upload failed')
          }
          const data = await response.json()
          return data.location
        },
        setup(editor) {
          // Sync content to underlying textarea on every change
          editor.on('change input keyup', () => {
            editor.save()
            onInput?.()
          })
          editor.on('init', () => editor.focus())
          onSetup?.(editor)
        },
      })

      resolve()
    }))
  })
}

/**
 * Remove a TinyMCE instance for a given editor element id.
 *
 * @param {string|HTMLElement} editorElOrId
 */
export function removeTinyMCE(editorElOrSelector) {
  if (!window.tinymce) return
  const editorEl = typeof editorElOrSelector === 'string'
    ? document.querySelector(editorElOrSelector)
    : editorElOrSelector
  const id = editorEl?.id || editorElOrSelector?.replace?.(/^#/, '')
  if (!id) return
  const editor = tinymce.get(id)
  if (editor) tinymce.remove(`#${id}`)
}

/**
 * Flush TinyMCE content to its underlying textarea.
 * Call before form submit to ensure the body field has the latest content.
 *
 * @param {string} editorId
 */
export function syncTinyMCE(editorId) {
  if (!window.tinymce) return
  if (editorId) {
    tinymce.get(editorId)?.save()
  } else {
    tinymce.get().forEach(ed => ed.save())
  }
}
